import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import * as RadixDialog from '@radix-ui/react-dialog'
import { useQuery } from '@tanstack/react-query'
import { AnimatePresence, motion } from 'motion/react'
import { Download, ExternalLink, Lock, Minus, Plus, X } from 'lucide-react'
import { ApiError, detailMessage } from '@/api/client'
import { useDocument } from '@/api/queries'
import type { DocumentDetail } from '@/api/types'
import { downloadOriginal, fetchOriginal } from '@/lib/documents'
import { ease, spring } from '@/lib/motion'
import { useUiStore } from '@/stores/uiStore'
import { Markdown } from '@/components/chat/Markdown'
import { FileBadge } from '@/components/documents/FileBadge'
import { Button } from '@/components/ui/Button'
import { IconButton } from '@/components/ui/IconButton'
import { Notice } from '@/components/ui/Notice'
import { RelativeTime } from '@/components/ui/RelativeTime'
import { Segmented } from '@/components/ui/Segmented'
import { Skeleton } from '@/components/ui/Skeleton'
import { Spinner } from '@/components/ui/Spinner'
import { Tooltip } from '@/components/ui/Tooltip'
import { Watermark } from './Watermark'

// pdf.js is large: load it only when a PDF is actually opened.
const PdfPages = lazy(() => import('./PdfPages'))

type Tab = 'original' | 'text'
const ZOOMS = [0.5, 0.67, 0.8, 0.9, 1, 1.1, 1.25, 1.5, 1.75, 2, 2.5, 3]
const DOCUMENT_ROUTE = /^\/documents\/\d+\/?$/

/**
 * Mounted once in the app frame. Opens over whatever page you're on (a cited
 * source in chat, the library, the admin console) so you never lose your place.
 * On /documents/:id the URL drives it, so viewer links can be shared.
 */
export function ViewerHost() {
  const viewer = useUiStore((s) => s.viewer)
  const closeViewer = useUiStore((s) => s.closeViewer)
  const navigate = useNavigate()
  const location = useLocation()
  const open = viewer.docId !== null

  const onOpenChange = (next: boolean) => {
    if (next) return
    if (DOCUMENT_ROUTE.test(location.pathname)) {
      // The library page closes the viewer when the id leaves the URL.
      if ((location.state as { fromLibrary?: boolean } | null)?.fromLibrary) navigate(-1)
      else navigate('/documents', { replace: true })
    } else {
      closeViewer()
    }
  }

  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open && (
          <RadixDialog.Portal forceMount>
            <RadixDialog.Overlay asChild forceMount>
              <motion.div
                // No backdrop blur: fading a blurred backdrop re-blurs the whole page every frame.
                className="fixed inset-0 z-40 bg-black/45"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3, ease: ease.out }}
              />
            </RadixDialog.Overlay>
            <RadixDialog.Content
              asChild
              forceMount
              aria-describedby={undefined}
              // Focus the viewer itself rather than its first button (which would pop its tooltip).
              onOpenAutoFocus={(event) => {
                event.preventDefault()
                ;(event.currentTarget as HTMLElement | null)?.focus()
              }}
            >
              <motion.div
                tabIndex={-1}
                className="fixed inset-0 z-50 flex flex-col overflow-hidden bg-canvas shadow-lift outline-none sm:inset-3 sm:rounded-[22px] lg:inset-5"
                // A transform string runs on the compositor (Web Animations), not per frame in JS.
                initial={{ opacity: 0, transform: 'translateY(28px) scale(0.98)' }}
                animate={{ opacity: 1, transform: 'translateY(0px) scale(1)' }}
                exit={{
                  opacity: 0,
                  transform: 'translateY(16px) scale(0.99)',
                  transition: { duration: 0.2, ease: ease.out },
                }}
                transition={{ duration: 0.42, ease: ease.apple }}
              >
                <DocumentViewer
                  key={viewer.docId}
                  docId={viewer.docId as number}
                  section={viewer.section}
                  nonce={viewer.nonce}
                />
              </motion.div>
            </RadixDialog.Content>
          </RadixDialog.Portal>
        )}
      </AnimatePresence>
    </RadixDialog.Root>
  )
}

function DocumentViewer({ docId, section, nonce }: { docId: number; section: string | null; nonce: number }) {
  const { data: doc, error, isPending, refetch } = useDocument(docId)
  const [tab, setTab] = useState<Tab | null>(null)
  const [zoom, setZoom] = useState(1)
  const [progress, setProgress] = useState<{ page: number; pages: number } | null>(null)
  const [blocked, setBlocked] = useState(0)

  const viewable = Boolean(doc?.original.viewable)
  const activeTab: Tab = tab ?? (viewable ? 'original' : 'text')
  const locked = doc ? !doc.can_download : true

  useEffect(() => {
    if (!doc) return
    const previous = document.title
    document.title = doc.title
    return () => {
      document.title = previous
    }
  }, [doc])

  // View-only: no printing or "Save page". (A deterrent — screenshots are covered by the watermark.)
  useEffect(() => {
    if (!locked) return
    const root = document.documentElement
    root.setAttribute('data-viewer-locked', '')
    const onKey = (event: KeyboardEvent) => {
      const key = event.key.toLowerCase()
      if ((event.ctrlKey || event.metaKey) && (key === 'p' || key === 's')) {
        event.preventDefault()
        event.stopPropagation()
        setBlocked((n) => n + 1)
      }
    }
    window.addEventListener('keydown', onKey, true)
    return () => {
      root.removeAttribute('data-viewer-locked')
      window.removeEventListener('keydown', onKey, true)
    }
  }, [locked])

  const zoomBy = (direction: 1 | -1) =>
    setZoom((current) => {
      const index = ZOOMS.findIndex((z) => z >= current - 0.001)
      const next = ZOOMS[Math.min(ZOOMS.length - 1, Math.max(0, (index === -1 ? ZOOMS.length - 1 : index) + direction))]
      return next ?? current
    })
  const onProgress = useCallback((page: number, pages: number) => setProgress({ page, pages }), [])

  return (
    <>
      <header className="glass-canvas relative z-20 flex shrink-0 flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3 shadow-[0_0.5px_0_rgb(var(--ink)/0.1)] sm:px-5">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          {doc ? <FileBadge fileType={doc.original.file_type} /> : <Skeleton className="size-10 rounded-[11px]" />}
          <div className="min-w-0">
            <RadixDialog.Title className="truncate text-[15px] font-semibold tracking-snug text-ink">
              {doc?.title ?? (error ? 'Document' : 'Opening…')}
            </RadixDialog.Title>
            {doc ? (
              <p className="flex min-w-0 items-center gap-1.5 truncate text-xs text-ink-faint">
                <span className="truncate">{doc.source_name}</span>
                {doc.department && <span aria-hidden>·</span>}
                {doc.department && <span className="truncate">{doc.department}</span>}
                <span aria-hidden>·</span>
                <RelativeTime value={doc.updated_at} prefix="updated" />
              </p>
            ) : (
              <Skeleton className="mt-1 h-3 w-40" />
            )}
          </div>
        </div>

        <div className="order-last flex w-full items-center justify-between gap-2 sm:order-none sm:w-auto sm:justify-end">
          {doc && viewable && (
            <Segmented<Tab>
              label="View as"
              size="sm"
              value={activeTab}
              onChange={setTab}
              options={[
                { value: 'original', label: 'Original' },
                { value: 'text', label: 'Text' },
              ]}
            />
          )}
          {doc && activeTab === 'original' && viewable && (
            <div className="flex items-center gap-0.5 rounded-full bg-ink/[0.05] p-0.5 dark:bg-white/[0.07]">
              <IconButton label="Zoom out" size="xs" onClick={() => zoomBy(-1)} disabled={zoom <= ZOOMS[0]!}>
                <Minus />
              </IconButton>
              <button
                type="button"
                onClick={() => setZoom(1)}
                className="h-7 min-w-[3.25rem] rounded-full px-1 text-xs font-medium tabular-nums text-ink-muted hover:text-ink"
                aria-label="Reset zoom"
              >
                {Math.round(zoom * 100)}%
              </button>
              <IconButton
                label="Zoom in"
                size="xs"
                onClick={() => zoomBy(1)}
                disabled={zoom >= ZOOMS[ZOOMS.length - 1]!}
              >
                <Plus />
              </IconButton>
            </div>
          )}
        </div>

        <div className="flex items-center gap-1">
          {doc?.url && (
            <Tooltip content="Open where this document lives (its own permissions apply)">
              <a
                href={doc.url}
                target="_blank"
                rel="noopener noreferrer"
                className="pressable inline-flex h-8 items-center gap-1.5 rounded-full px-3 text-[13px] font-medium text-ink-muted hover:bg-subtle hover:text-ink"
              >
                <ExternalLink className="size-3.5" aria-hidden />
                <span className="hidden md:inline">Source</span>
              </a>
            </Tooltip>
          )}
          {doc && <DownloadControl doc={doc} />}
          <RadixDialog.Close asChild>
            <IconButton label="Close" tooltip="Close (Esc)" variant="subtle">
              <X />
            </IconButton>
          </RadixDialog.Close>
        </div>
      </header>

      <div
        className={`relative min-h-0 flex-1 bg-subtle/60 dark:bg-black/30 ${locked ? 'select-none' : ''}`}
        onContextMenu={locked ? (event) => event.preventDefault() : undefined}
        onCopy={locked ? (event) => event.preventDefault() : undefined}
      >
        {isPending ? (
          <Centered>
            <Spinner className="size-5" label="Loading document" />
          </Centered>
        ) : error || !doc ? (
          <Centered>
            <Notice
              tone={error instanceof ApiError && error.isNotFound ? 'locked' : 'danger'}
              title="This document can’t be opened"
              action={
                <Button size="sm" variant="outline" onClick={() => void refetch()}>
                  Try again
                </Button>
              }
              className="max-w-md"
            >
              {detailMessage(error)}
            </Notice>
          </Centered>
        ) : (
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={activeTab}
              className="absolute inset-0"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2, ease: ease.out }}
            >
              {activeTab === 'original' && viewable ? (
                <OriginalView doc={doc} zoom={zoom} onZoomChange={setZoom} onProgress={onProgress} />
              ) : (
                <TextView doc={doc} section={section} nonce={nonce} />
              )}
            </motion.div>
          </AnimatePresence>
        )}
        {doc?.watermark && <Watermark text={doc.watermark} />}

        {/* Centred by flex: motion owns `transform`, so translate-x classes would be overwritten. */}
        <div className="pointer-events-none absolute inset-x-0 bottom-4 z-20 flex justify-center">
          <AnimatePresence>
            {doc && activeTab === 'original' && viewable && progress && progress.pages > 1 && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 8 }}
                className="glass rounded-full px-3 py-1 text-xs font-medium tabular-nums text-ink-muted shadow-popover"
                aria-live="polite"
              >
                Page {progress.page} of {progress.pages}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <BlockedToast nonce={blocked} />
      </div>
    </>
  )
}

function DownloadControl({ doc }: { doc: DocumentDetail }) {
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState<string | null>(null)
  if (!doc.can_download) {
    return (
      <Tooltip content="Downloading is turned off for your account. You can read it here.">
        <span
          tabIndex={0}
          className="inline-flex h-8 items-center gap-1.5 rounded-full px-3 text-[13px] font-medium text-ink-faint"
        >
          <Lock className="size-3.5" aria-hidden />
          <span className="hidden md:inline">View only</span>
          <span className="sr-only md:hidden">View only</span>
        </span>
      </Tooltip>
    )
  }
  if (!doc.original.kind) return null
  return (
    <Tooltip content={failed ?? 'Download the original file'}>
      <Button
        size="sm"
        variant="secondary"
        disabled={busy}
        onClick={async () => {
          setBusy(true)
          setFailed(null)
          try {
            await downloadOriginal(doc.id, doc.title)
          } catch (error) {
            setFailed(detailMessage(error))
          } finally {
            setBusy(false)
          }
        }}
      >
        {busy ? <Spinner className="size-3.5" /> : <Download className="size-3.5" aria-hidden />}
        <span className="hidden md:inline">Download</span>
      </Button>
    </Tooltip>
  )
}

function OriginalView({
  doc,
  zoom,
  onZoomChange,
  onProgress,
}: {
  doc: DocumentDetail
  zoom: number
  onZoomChange: (zoom: number) => void
  onProgress: (page: number, pages: number) => void
}) {
  const original = useQuery({
    queryKey: ['document-original', doc.id],
    queryFn: ({ signal }) => fetchOriginal(doc.id, signal),
    staleTime: Infinity,
    // Don't keep the file around once the viewer closes.
    gcTime: 0,
    retry: 1,
  })

  if (original.isPending) {
    return (
      <Centered>
        <Spinner className="size-5" label="Opening document" />
      </Centered>
    )
  }
  if (original.error) {
    return (
      <Centered>
        <Notice tone="warning" title="Can’t show the original" className="max-w-md">
          {detailMessage(original.error)} The Text tab still has the full contents.
        </Notice>
      </Centered>
    )
  }
  if (doc.original.kind === 'pdf') {
    return (
      <Suspense
        fallback={
          <Centered>
            <Spinner className="size-5" label="Opening document" />
          </Centered>
        }
      >
        <PdfPages data={original.data.data} zoom={zoom} onZoomChange={onZoomChange} onProgress={onProgress} />
      </Suspense>
    )
  }
  return <ImageView data={original.data.data} type={original.data.type} zoom={zoom} title={doc.title} />
}

function ImageView({ data, type, zoom, title }: { data: ArrayBuffer; type: string; zoom: number; title: string }) {
  const url = useMemo(() => URL.createObjectURL(new Blob([data], { type: type || 'image/png' })), [data, type])
  useEffect(() => () => URL.revokeObjectURL(url), [url])
  return (
    <div className="absolute inset-0 overflow-auto overscroll-contain p-6">
      <div className="flex min-h-full min-w-full items-center justify-center">
        <img
          src={url}
          alt={title}
          draggable={false}
          className="rounded-md bg-white shadow-lift"
          style={
            zoom === 1
              ? { maxWidth: '100%', maxHeight: 'calc(100dvh - 10rem)' }
              : { width: `${zoom * 100}%`, maxWidth: 'none' }
          }
        />
      </div>
    </div>
  )
}

const normalise = (text: string) => text.replace(/\s+/g, ' ').trim().toLowerCase()

function TextView({ doc, section, nonce }: { doc: DocumentDetail; section: string | null; nonce: number }) {
  const ref = useRef<HTMLDivElement>(null)

  // Opened from a cited source: scroll to that section and highlight it.
  useEffect(() => {
    const root = ref.current
    const target = section?.split('>').pop()
    if (!root || !target?.trim()) return
    const wanted = normalise(target)
    const heading = Array.from(root.querySelectorAll<HTMLElement>('h1,h2,h3,h4,h5,h6')).find((el) => {
      const text = normalise(el.textContent ?? '')
      return text === wanted || text.includes(wanted)
    })
    if (!heading) return
    const frame = requestAnimationFrame(() => {
      heading.scrollIntoView({ block: 'start', behavior: 'smooth' })
      heading.setAttribute('data-viewer-target', '')
    })
    const timer = window.setTimeout(() => heading.removeAttribute('data-viewer-target'), 2600)
    return () => {
      cancelAnimationFrame(frame)
      window.clearTimeout(timer)
      heading.removeAttribute('data-viewer-target')
    }
  }, [section, nonce])

  return (
    <div className="absolute inset-0 overflow-y-auto overscroll-contain">
      <article
        ref={ref}
        className="mx-auto my-6 w-[min(100%-2rem,52rem)] rounded-2xl bg-surface px-6 py-8 shadow-card sm:px-12 sm:py-12"
      >
        {doc.markdown.trim() ? (
          <Markdown
            content={doc.markdown}
            citations={false}
            className="[&_h1]:scroll-mt-6 [&_h2]:scroll-mt-6 [&_h3]:scroll-mt-6"
          />
        ) : (
          <p className="text-sm text-ink-muted">No readable text was extracted from this document.</p>
        )}
      </article>
    </div>
  )
}

function BlockedToast({ nonce }: { nonce: number }) {
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    if (!nonce) return
    setVisible(true)
    const timer = window.setTimeout(() => setVisible(false), 2400)
    return () => window.clearTimeout(timer)
  }, [nonce])
  return (
    <div className="pointer-events-none absolute inset-x-4 top-4 z-30 flex justify-center">
      <AnimatePresence>
        {visible && (
          <motion.div
            role="status"
            initial={{ opacity: 0, y: -12, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={spring.snappy}
            className="flex items-center gap-2 rounded-full bg-ink/90 px-4 py-2 text-[13px] font-medium text-canvas shadow-popover backdrop-blur-md"
          >
            <Lock className="size-3.5 shrink-0" aria-hidden />
            Printing and saving are turned off
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

function Centered({ children }: { children: ReactNode }) {
  return <div className="absolute inset-0 flex items-center justify-center p-6 text-ink-muted">{children}</div>
}

/** Small "Open" control used in lists. */
export function ViewDocumentButton({
  docId,
  section,
  children,
  className,
}: {
  docId: number
  section?: string | null
  children: ReactNode
  className?: string
}) {
  const openViewer = useUiStore((s) => s.openViewer)
  return (
    <button type="button" className={className} onClick={() => openViewer(docId, section ?? null)}>
      {children}
    </button>
  )
}
